import DataLoader from 'dataloader'

import configs from '@acre/config'

import {
  BulkCreateRemoLeadsInput,
  CaseAssigneesInput,
  CaseFlagReviewInput,
  CaseInput,
  CaseUpdateStatusResponse,
  CaseVerification,
  CaseVersion,
  CaseVersionsTimelineResponse,
  Client,
  GetCaseReferralListFilterInput,
  GetCaseReferralListResponse,
  GetCaseRequestInput,
  GetMiCaseListFilterInput,
  GetMiCaseListResponse,
  GetReferredCaseListFilterInput,
  Maybe,
  ReferralCaseInput,
  Survey,
} from '../generated/resolvers'
import { mortgageLoader } from '../loaders/mortgage'
import request from '../requesters/default'
import {
  CdmCase,
  CdmCreateCaseFlagReviewResponse,
  CdmCreateCaseResponse,
  CdmGetCaseResponse,
  CdmUpdateCaseResponse,
  CdmUpdateCaseStatusResponse,
} from '../service/luther/model'
import { UUID } from '../types'
import { resetVerificationCacheInvalidation, setVerificationCacheInvalidation, verificationCacheInvalidation } from '../utils'
import { objectToUrlParams } from '../utils/helpers'
import { formatCase } from '../utils/schemaMapping/case'
import { SummaryLoader } from './summary'

const { API1_URL, CLIENT_PORTAL_API_URL, CLIENT_PORTAL_OATHKEEPER_API_URL } = configs

const fetchCasesBatchFn = async (ids: UUID[], useClientApi?: boolean, hydrateRelatedCases?: Maybe<boolean>) => {
  const uniqueIds = Array.from(new Set(ids))
  const params = {
    case_ids: uniqueIds,
    case_details: true,
    hydrate_related_cases: hydrateRelatedCases
  }
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  const baseApiUrl =
    useOathKeeper === 'true' ? CLIENT_PORTAL_OATHKEEPER_API_URL : useClientApi ? CLIENT_PORTAL_API_URL : undefined
  const axiosOpts = { baseURL: baseApiUrl, params }
  const response = await request.get<{ cases: Array<CdmCase> }>('/case', axiosOpts)

  const cases = response.data.cases || []

  return cases.map(formatCase)
}

export const CaseLoader = new DataLoader(async (keys: { id: string; hydrateRelatedCases?: Maybe<boolean> }[]) => {
  const ids = keys.map((key) => key.id)
  // Hydrate related cases when explicitly requested as there is a performance overhead
  const hydrateRelatedCases = Boolean(keys.find((key) => key.hydrateRelatedCases))
  const cases = await fetchCasesBatchFn(ids, undefined, hydrateRelatedCases)
  return ids.map((id) => cases.find((caseData) => caseData.id === id)) as CaseVersion[]
}, {
  cacheKeyFn: (key) => {
    // Use a consistent cache key format that groups by hydration preference
    // This ensures that cases with different hydration settings are cached separately
    // but clearing by ID will clear all variations of that case
    return `${key.id}:${key.hydrateRelatedCases || false}`
  }
})

// Helper function to clear all cache variations for a specific case ID
export const clearCaseFromCache = (id: string) => {
  // Clear both hydrated and non-hydrated versions
  CaseLoader.clear({ id, hydrateRelatedCases: false })
  CaseLoader.clear({ id, hydrateRelatedCases: true })
}

export const fetchCase = async (id: UUID, hydrateRelatedCases?: Maybe<boolean>): Promise<CaseVersion> => {
  return await CaseLoader.load({ id, hydrateRelatedCases })
}

export const ClientCaseLoader = new DataLoader(
  (ids: string[]) => fetchCasesBatchFn(ids, true),
  { cache: true }
)

export const fetchClientCase = async (id: UUID): Promise<CaseVersion> => {
  return await ClientCaseLoader.load(id)
}

// DataLoader for caching cases by client ID
export const CasesByClientLoader = new DataLoader(
  async (clientIds: string[]) => {
    const results = await Promise.all(
      clientIds.map(async (clientId) => {
        const response = await request.get<CdmGetCaseResponse>(`/case?case_details=false&filter_client_ids=${clientId}`)

        if (!response.data.cases) {
          return []
        }

        const caseIds = response.data.cases.reduce((acc, caseData) => {
          if (caseData.case_id) {
            acc.push(caseData.case_id)
          }
          return acc
        }, [] as string[])

        return await CaseLoader.loadMany(caseIds.map((id) => ({ id })))
      })
    )

    return results
  },
  { cache: true }
)

export const fetchAllCases = async (params?: Maybe<GetCaseRequestInput>) => {
  const reqParams = {
    filter_order_reverse: true,
    page_size: 20,
    case_details: false,
    ...params,
  }

  const response = await request.get<CdmGetCaseResponse>('/case', { params: reqParams })

  if (!response.data.cases) {
    return null
  }

  const caseIds = response.data.cases.reduce((acc, caseData) => {
    if (caseData.case_id) {
      acc.push(caseData.case_id)
    }

    return acc
  }, [] as string[])

  const formattedResponse = {
    cases: await CaseLoader.loadMany(caseIds.map((id) => ({ id }))),
    next_page: response.data.next_page,
  }

  return formattedResponse
}

export const fetchCasesForClient = async (clientId: string) => {
  return await CasesByClientLoader.load(clientId)
}

export const addCase = async (details: CaseInput) => {
  const response = await request.post<CdmCreateCaseResponse>(`/case`, {
    case: { details },
  })

  // Add new case to the cache
  if (response.data.case?.case_id) {
    CaseLoader.prime({ id: response.data.case.case_id }, formatCase(response.data.case))
  }

  return response.data.case ? formatCase(response.data.case) : null
}

export const createReferralCase = async (input: ReferralCaseInput) => {
  const response = await request.post<CdmCreateCaseResponse>(`/case/referral`, input)

  if (!response.data.case) {
    return null
  }

  const formattedCase = formatCase(response.data.case)

  // Add new case to the cache
  CaseLoader.prime({ id: formattedCase.id }, formattedCase)

  return formattedCase
}

export const updateCase = async (input: CaseInput, id: string) => {
  const response = await request.patch<CdmUpdateCaseResponse>(`/case/${id}`, {
    details: input,
  })

  if (!response.data.case) {
    return null
  }

  clearCaseFromCache(id)
  SummaryLoader.clearAll()
  CasesByClientLoader.clearAll()

  const formattedCase = formatCase(response.data.case)
  CaseLoader.prime({ id: formattedCase.id }, formattedCase)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return formattedCase
}

export const updateCaseAssignees = async (input: CaseAssigneesInput) => {
  const response = await request.post('/case/assign', input)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response.data
}

export const bulkCreateRemoLeads = async (input: BulkCreateRemoLeadsInput) => {
  const response = await request.post('/case/leads', input)

  return response.data
}

export const fetchVerifications = async (
  case_id: string,
  show_pass?: Maybe<boolean>,
  show_documents?: Maybe<boolean>,
) => {
  const url = `/case/${case_id}/verifications`

  // Variable to determine if we need to add ignore_cache param
  // Refer to comments in caseVerificationInvalidation.ts
  const isVerificationCacheInvalidated = verificationCacheInvalidation()

  const params: Record<string, any> = {
    case_verifications: true,
    document_requirements: show_documents ?? false,
  };

  // Add show_pass if provided
  if (show_pass !== undefined) {
    params.show_pass = show_pass
  }

  // Add ignore_cache if reactive variable is true
  if (isVerificationCacheInvalidated) {
    params.ignore_cache = true
  }

  const response = await request.get<CaseVerification>(url, { params })

  // Reset the invalidation variable after using, after the request to avoid race conditions
  if (isVerificationCacheInvalidated) {
    resetVerificationCacheInvalidation()
  }

  return response.data
};

export const updateCaseStatus = async (case_id: UUID, input: any) => {
  const response = await request.post<CdmUpdateCaseStatusResponse>(`/case/${case_id}/case_transition`, {
    ...input,
    case_id,
  })

  if (!response.data.case) {
    return null
  }

  clearCaseFromCache(case_id)
  CaseLoader.prime({ id: case_id }, formatCase(response.data.case))

  SummaryLoader.clearAll()
  CasesByClientLoader.clearAll()

  // Clear cached mortgage data on case
  // as BE updates some of it on case status update
  // so we'd like to re-fetch it
  mortgageLoader.clear({ filter_case_id: case_id })

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return { ...response.data, case: formatCase(response.data.case) } as CaseUpdateStatusResponse
}

// Create case flag review endpoint returns an empty object
export const createCaseFlagReview = async (input: CaseFlagReviewInput) => {
  const response = await request.post<CdmCreateCaseFlagReviewResponse>(
    `/case/${input.case_id}/flag/${input.flag_id}/review`,
    {
      reviewed_case_version: input.reviewed_case_version,
      review_passed: input.review_passed,
      skip_future_reviews: input.skip_future_reviews,
      review_grade: input.review_grade,
      // Don't send reviewer_notes if it's empty
      ...(input.reviewer_notes ? { reviewer_notes: input.reviewer_notes } : {}),
    },
  )

  clearCaseFromCache(input.case_id)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response.data
}

/**
 * Fetches cases list from the dashboard API
 */
export const getMiCaseList = async (input: GetMiCaseListFilterInput): Promise<GetMiCaseListResponse> => {
  const params = objectToUrlParams(input)

  // Use a POST request instead of GET to avoid URL hitting the length limits when clients have 200+ filters
  const response = await request.post<GetMiCaseListResponse>('/dashboard', params,
    {
      baseURL: configs.SEARCH_API_URL,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }
  )

  return response.data
}

/**
 * Fetches cases list from the dashboard API
 */
export const referredCaseList = async (input: GetCaseReferralListFilterInput): Promise<GetCaseReferralListResponse> => {
  const params = objectToUrlParams(input)

  // Use a POST request instead of GET to avoid URL hitting the length limits when clients have 200+ filters
  const response = await request.post<GetCaseReferralListResponse>('/referrals', params,
    {
      baseURL: configs.SEARCH_API_URL,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }
  )

  return response.data
}

export const getReferredCaseList = async (input: GetReferredCaseListFilterInput) => {
  const response = await request.get<GetMiCaseListResponse>('/mi/firm/case/referrals', {
    baseURL: `${API1_URL}/acre`,
    params: input,
  })
  const leadCases = response.data?.cases?.map((item) => {
    const leadCase = {
      ...item,
      introducerClients: item.clients as Client[],
    }

    return leadCase
  })

  return { ...response.data, cases: leadCases }
}

export const getCaseVersionsTimeline = async (case_id: string) => {
  const response = await request.get<CaseVersionsTimelineResponse>(`/case/${case_id}/version/timeline`, {
    baseURL: `${API1_URL}/acre`,
  })

  return response.data
}

export const getCaseByVersion = async (case_id: string, version: number, show_history?: Maybe<Number>) => {
  const url = show_history
    ? `/case/${case_id}/version/${version}?show_history=${show_history}`
    : `/case/${case_id}/version/${version}`

  const response = await request.get(url, {
    baseURL: `${API1_URL}/acre`,
  })

  return response?.data?.case && formatCase(response.data.case)
}

export const getCaseSurvey = async (case_id: string) => {
  const response = await request.get<Survey>(`/case/${case_id}/survey`, {
    baseURL: `${API1_URL}/acre`,
  })

  return response.data
}
